import { type FC, useEffect, useState } from 'react';
import type { Node as RFNode } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import { NodeStatus, NODE_TYPE_COLORS, NODE_STATUS_LABELS } from '@/types/node.types';
import type { Priority } from '@/types/node.types';
import nodesApi from '@/api/nodes.api';
import { useCanvasStore } from '@/stores/canvas.store';

interface NodeSheetProps {
  node: RFNode<NodeData> | null;
  onClose: () => void;
  onOpenComments: (nodeId: string) => void;
}

const NODE_TYPE_LABELS: Record<string, string> = {
  Vision: 'Vision',
  Objective: 'Objectif',
  Activity: 'Activité',
  Risk: 'Risque',
  Opportunity: 'Opportunité',
  Resource: 'Ressource',
  KPI: 'KPI',
  Stakeholder: 'Partie prenante',
};

export const NodeSheet: FC<NodeSheetProps> = ({ node, onClose, onOpenComments }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>(3);
  const [status, setStatus] = useState<NodeStatus>(NodeStatus.Active);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!node) return;
    setTitle(node.data.title);
    setDescription(node.data.description ?? '');
    setPriority(node.data.priority);
    setStatus(node.data.status);
  }, [node]);

  if (!node) return null;

  const color = NODE_TYPE_COLORS[node.data.type] || '#6B7280';

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await nodesApi.update(node.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        status,
      });
      // Update store
      useCanvasStore.setState((state) => ({
        nodes: state.nodes.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, ...updated } }
            : n,
        ),
      }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Supprimer "${node.data.title}" ?`)) return;
    await nodesApi.delete(node.id);
    useCanvasStore.setState((state) => ({
      nodes: state.nodes.filter((n) => n.id !== node.id),
      edges: state.edges.filter((e) => e.source !== node.id && e.target !== node.id),
    }));
    onClose();
  };

  const chip = (active: boolean, bg: string): React.CSSProperties => ({
    padding: '8px 14px',
    borderRadius: 999,
    border: `1.5px solid ${active ? bg : 'var(--color-border)'}`,
    backgroundColor: active ? `${bg}22` : 'transparent',
    color: active ? bg : 'var(--color-text-muted)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  });

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 900,
        }}
      />
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: '92vh',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.4)',
          zIndex: 910,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        {/* Grabber */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'var(--color-border)' }} />
        </div>

        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 16px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              width: 10,
              height: 40,
              borderRadius: 3,
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {NODE_TYPE_LABELS[node.data.type]}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {node.data.title}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--color-text-muted)', padding: 4 }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Titre
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: '100%',
              marginTop: 6,
              padding: '10px 12px',
              fontSize: 15,
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              outline: 'none',
            }}
          />

          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, display: 'block' }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              marginTop: 6,
              padding: '10px 12px',
              fontSize: 14,
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />

          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, display: 'block' }}>
            Priorité
          </label>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 2, 3, 4, 5].map((p) => (
              <button
                key={p}
                onClick={() => setPriority(p as Priority)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  border: `1.5px solid ${priority === p ? color : 'var(--color-border)'}`,
                  backgroundColor: priority === p ? `${color}22` : 'transparent',
                  color: priority === p ? color : 'var(--color-text-muted)',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                P{p}
              </button>
            ))}
          </div>

          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, display: 'block' }}>
            Statut
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {Object.values(NodeStatus).map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                style={chip(status === s, 'var(--color-primary)')}
              >
                {NODE_STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          <button
            onClick={() => onOpenComments(node.id)}
            style={{
              width: '100%',
              marginTop: 20,
              padding: 12,
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              borderRadius: 12,
              color: 'var(--color-text)',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            💬 Voir les commentaires
          </button>

          <button
            onClick={handleDelete}
            style={{
              width: '100%',
              marginTop: 8,
              padding: 12,
              backgroundColor: 'transparent',
              border: '1px solid #EF4444',
              borderRadius: 12,
              color: '#EF4444',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Supprimer le noeud
          </button>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: 16,
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: 14,
              borderRadius: 12,
              border: '1px solid var(--color-border)',
              backgroundColor: 'transparent',
              color: 'var(--color-text)',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            style={{
              flex: 2,
              padding: 14,
              borderRadius: 12,
              border: 'none',
              backgroundColor: color,
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              opacity: saving || !title.trim() ? 0.5 : 1,
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </>
  );
};

export default NodeSheet;
